import { Checkbox, type CheckboxProps } from '@progress/kendo-react-inputs'
import React from 'react'

import { VStack } from '~/components/elements/VStack'

import styles from './RowDisplayToggleFormItem.css'
import {
  RowDisplayToggleFormSubItem,
  type RowDisplayToggleFormSubItemProps,
} from './RowDisplayToggleFormSubItem'

type RowDisplayToggleItem<RowId extends number | string> = {
  label: string
  subItems: RowDisplayToggleFormSubItemProps<RowId>['subItem'][]
}

export type RowDisplayToggleFormItemProps<RowId extends number | string> = {
  item: RowDisplayToggleItem<RowId>
  onChange?: (value: boolean, ids: RowId[]) => void
}

export const RowDisplayToggleFormItem = <RowId extends number | string>({
  item,
  onChange,
}: RowDisplayToggleFormItemProps<RowId>) => {
  const isAllChecked = React.useMemo<CheckboxProps['checked']>(
    () =>
      item.subItems.every((subItem) => subItem.isVisible)
        ? true
        : item.subItems.every((subItem) => !subItem.isVisible)
          ? false
          : null,
    [item.subItems],
  )

  const handleAllChange = React.useCallback<EH<CheckboxProps, 'onChange'>>(
    (event) => {
      const ids = item.subItems.map((subItem) => subItem.id)

      onChange?.(event.value, ids)
    },
    [onChange, item.subItems],
  )

  return (
    <VStack spacing={2}>
      <Checkbox
        checked={isAllChecked}
        label={item.label}
        onChange={handleAllChange}
      />

      <VStack className={styles.subItems} spacing={2}>
        {item.subItems.map((subItem) => (
          <RowDisplayToggleFormSubItem
            key={subItem.id}
            subItem={subItem}
            onChange={onChange}
          />
        ))}
      </VStack>
    </VStack>
  )
}
